"use client";

import { buildControllerRules } from "@gettactic/helpers/src/hook-form/buildControllerRules";
import React from "react";
import { Controller, FieldValues } from "react-hook-form";
import { UserSelectInput } from "./UserSelectInput";
import { HookFormUserSelectInputProps } from "./UserSelectInput.types";

/**
 * `UserSelectInput` bound to a react-hook-form field holding the user id, or an array of ids
 * with `multiselect`.
 */
export const HookFormUserSelectInput = <T extends FieldValues>({
  form,
  name,
  rules,
  onChange,
  multiselect = false,
  ...props
}: HookFormUserSelectInputProps<T>): React.ReactElement => {
  return (
    <Controller
      control={form.control}
      name={name}
      rules={buildControllerRules({
        form,
        name,
        rules,
        isRequired: props.isRequired
      })}
      render={({ field, fieldState }) => {
        if (multiselect) {
          // For multiselect, field.value should be an array of strings
          const values = Array.isArray(field.value) ? field.value : [];
          return (
            <UserSelectInput
              ref={field.ref}
              {...props}
              multiselect={true}
              values={values}
              onChangeValues={(newValues) => {
                field.onChange(newValues);
                onChange?.(newValues);
              }}
              error={fieldState.error?.message}
            />
          );
        } else {
          // For single select, field.value should be a string
          const value = typeof field.value === "string" ? field.value : "";
          return (
            <UserSelectInput
              ref={field.ref}
              {...props}
              multiselect={false}
              value={value}
              onChangeValue={(newValue) => {
                field.onChange(newValue);
                onChange?.(newValue);
              }}
              error={fieldState.error?.message}
            />
          );
        }
      }}
    />
  );
};
